import { css, html, LitElement } from 'lit'
import { property, state } from 'lit/decorators.js'
import { safeCustomElement } from '@/utilities/custom-element'
@safeCustomElement('mdc-switch')
export class MdcSwitch extends LitElement {
  @property({ type: String }) size: 'sm' | 'md' | 'lg' = 'md'
  @property({ type: Boolean }) disabled = false
  @property({ type: Boolean }) checked = false

  @state() private internalChecked = false

  static styles = css`
		:host {
			display: inline-block;
			font-family: var(--typography-font-family-sans);
		}

		.mdc-switch {
			display: inline-flex;
			align-items: center;
			cursor: pointer;
			user-select: none;
		}

		.mdc-switch__input {
			position: absolute;
			opacity: 0;
			width: 0;
			height: 0;
		}

		.mdc-switch__slider {
			position: relative;
			display: flex;
			align-items: center;
			background-color: var(--color-functional-gray-200);
			border-radius: var(--radius-full, 9999px);
			transition: background-color 0.2s;
		}

		.mdc-switch--size-sm .mdc-switch__slider {
			width: 2rem;
			height: 1.125rem;
		}

		.mdc-switch--size-md .mdc-switch__slider {
			width: 2.5rem;
			height: 1.375rem;
		}

		.mdc-switch--size-lg .mdc-switch__slider {
			width: 3rem;
			height: 1.625rem;
		}

		.mdc-switch__slider-thumb {
			position: absolute;
			background-color: var(--color-functional-gray-white);
			border-radius: var(--radius-full, 9999px);
			transition: transform 0.2s;
			box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
		}

		.mdc-switch--size-sm .mdc-switch__slider-thumb {
			width: 0.875rem;
			height: 0.875rem;
			left: 0.125rem;
		}

		.mdc-switch--size-md .mdc-switch__slider-thumb {
			width: 1.125rem;
			height: 1.125rem;
			left: 0.125rem;
		}

		.mdc-switch--size-lg .mdc-switch__slider-thumb {
			width: 1.375rem;
			height: 1.375rem;
			left: 0.125rem;
		}

		.mdc-switch__input:checked + .mdc-switch__slider {
			background-color: var(--color-functional-gray-black);
		}

		.mdc-switch--size-sm .mdc-switch__input:checked + .mdc-switch__slider .mdc-switch__slider-thumb {
			transform: translateX(0.875rem);
		}

		.mdc-switch--size-md .mdc-switch__input:checked + .mdc-switch__slider .mdc-switch__slider-thumb {
			transform: translateX(1.125rem);
		}

		.mdc-switch--size-lg .mdc-switch__input:checked + .mdc-switch__slider .mdc-switch__slider-thumb {
			transform: translateX(1.375rem);
		}

		.mdc-switch__input:disabled + .mdc-switch__slider {
			background-color: var(--color-functional-gray-50);
			cursor: not-allowed;
		}

		.mdc-switch__input:disabled + .mdc-switch__slider .mdc-switch__slider-thumb {
			background-color: var(--color-functional-gray-200);
		}

		.mdc-switch__input:disabled ~ * {
			cursor: not-allowed;
		}
	`

  private handleChange(e: Event) {
    const input = e.target as HTMLInputElement
    this.internalChecked = input.checked
    this.dispatchEvent(
      new CustomEvent('change', {
        detail: { checked: this.internalChecked },
        bubbles: true,
        composed: true,
      })
    )
  }

  updated(changedProperties: Map<PropertyKey, unknown>) {
    if (changedProperties.has('checked')) {
      this.internalChecked = this.checked
    }
  }

  render() {
    const isChecked = this.checked !== undefined ? this.checked : this.internalChecked
    const className = `mdc-switch mdc-switch--size-${this.size}`

    return html`
			<label class=${className}>
				<input
					type="checkbox"
					class="mdc-switch__input"
					?checked=${isChecked}
					?disabled=${this.disabled}
					@change=${this.handleChange}
				/>
				<span class="mdc-switch__slider">
					<span class="mdc-switch__slider-thumb"></span>
				</span>
			</label>
		`
  }
}
